import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * BookingFlip Component
 * Props:
 *  initialDate: Date
 *  selectedTime: Date
 *  duration: number
 *  headerImage: string
 *  onConfirm: function
 *  onClose: function
 */

/* Helper: format full date + time */
function fullDateString(date, time) {
  const d = new Date(date);

  if (time && typeof time.getHours === "function") {
    d.setHours(time.getHours(), time.getMinutes(), 0, 0);
  }

  return d.toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/* Helper: render input with valid label association */
function renderInput(name, label, value, onChange, type = "text", isRequired = false) {
  const id = `bf-${name}`;
  return (
    <div key={name}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        required={isRequired}
        onChange={onChange}
        className="mt-1 w-full p-2 border rounded"
      />
    </div>
  );
}

export default function BookingFlip({
  initialDate,
  selectedTime,
  duration,
  headerImage,
  loading,
  onConfirm,
  onClose,
}) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    jobTitle: "",
    company: "",
    notes: "",
  });

  const formRef = useRef(null);

  // Autofocus
  useEffect(() => {
    if (formRef.current) {
      const firstField = formRef.current.querySelector("input, textarea");
      if (firstField) firstField.focus();
    }
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();

    const finalDate = new Date(initialDate);

    if (selectedTime) {
      finalDate.setHours(
        selectedTime.getHours(),
        selectedTime.getMinutes(),
        0,
        0,
      );
    }

    onConfirm({
      ...form,
      datetime: finalDate,
      duration,
    });
  }

  return (
    <div className="w-full">

      {/* HEADER */}
      <div className="flex items-center gap-3 mb-4 border-b pb-3">
        {headerImage ? (
          <img
            src={headerImage}
            alt="Meeting"
            className="w-10 h-10 rounded object-contain"
          />
        ) : (
          <div className="w-10 h-10 rounded bg-gray-200" />
        )}

        <div>
          <div className="text-sm font-medium text-gray-700">
            {fullDateString(initialDate, selectedTime)}
          </div>
          <div className="text-xs text-teal-600">Zoom</div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="ml-auto text-sm border rounded px-3 py-1"
          aria-label="Back"
        >
          &lt; Back
        </button>
      </div>

      {/* FORM */}
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
        {renderInput("fullName", "Full name *", form.fullName, handleChange, "text", true)}
        {renderInput(
          "email",
          "Email address *",
          form.email,
          handleChange,
          "email",
          true,
        )}
        {renderInput("jobTitle", "Job title", form.jobTitle, handleChange)}
        {renderInput("company", "Company name", form.company, handleChange)}

        {/* NOTES FIELD */}
        <div>
          <label
            htmlFor="bf-notes"
            className="block text-sm font-medium text-gray-700"
          >
            What would you like to speak about?
            <textarea
              id="bf-notes"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={4}
              className="mt-1 w-full p-2 border rounded"
            />
          </label>
        </div>

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={loading}
          className={`px-4 py-2 text-white rounded transition-opacity ${
            loading
              ? 'bg-brand-green-400 cursor-not-allowed opacity-60'
              : 'bg-brand-green-600 hover:bg-brand-green-700'
          }`}
        >
          {loading ? 'Sending…' : 'Confirm'}
        </button>
      </form>
    </div>
  );
}

/* PROP TYPES */
BookingFlip.propTypes = {
  initialDate: PropTypes.instanceOf(Date).isRequired,
  selectedTime: PropTypes.instanceOf(Date),
  duration: PropTypes.number,
  headerImage: PropTypes.string,
  loading: PropTypes.bool,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
};

BookingFlip.defaultProps = {
  selectedTime: null,
  duration: 30,
  headerImage: "",
  loading: false,
  onConfirm: () => {},
  onClose: () => {},
};
